import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { MortgageSettings, DEFAULT_MORTGAGE, SIM_MAX_MONTHS } from '../money.model';
import { formatCents } from '../format';
import { toCents, toBasisPoints, todayISO } from '../input';
import { LinePoint } from '../charts/line-chart.component';
import { targetDate } from '../savings/savings.calc';
import { MonthRow, monthlyPayment, amortize } from './mortgage.calc';

/** One row of the year-by-year table. */
export interface YearRow {
  year: number; interestCents: number; principalCents: number; balanceCents: number;
}

/** A schedule collapsed to year ends. Exported for the spec. */
export function annualRows(rows: MonthRow[]): YearRow[] {
  const years: YearRow[] = [];
  for (let i = 0; i < rows.length; i += 12) {
    const slice = rows.slice(i, i + 12);
    years.push({
      year: i / 12 + 1,
      interestCents: slice.reduce((s, r) => s + r.interestCents, 0),
      principalCents: slice.reduce((s, r) => s + r.principalCents, 0),
      balanceCents: slice[slice.length - 1].balanceCents,
    });
  }
  return years;
}

/** A chart-ready year series: points paired with their pre-formatted labels. */
export interface YearSeries { points: LinePoint[]; displayValues: string[]; }

function padOne(series: YearSeries, length: number): YearSeries {
  const points = series.points.slice();
  const displayValues = series.displayValues.slice();
  for (let i = points.length; i < length; i++) {
    points.push({ label: `${i + 1}y`, value: 0 });
    displayValues.push(formatCents(0));
  }
  return { points, displayValues };
}

/**
 * Pads the shorter of two year-end series with zero-balance years so both
 * reach the same length. Exported for the spec.
 *
 * `lineLayout` places each series' x-coordinates at `index / (length - 1)`
 * of THAT series' own length, so two schedules of different lengths get
 * stretched to fill the same width and appear to reach zero on the same
 * date — a 19-year payoff and a 30-year payoff would look identical. A zero
 * balance for the years after a schedule has already paid off is the
 * literal truth, not a cosmetic filler, so padding the shorter series to the
 * longer one's length puts both on the same x-scale.
 */
export function padToSameHorizon(a: YearSeries, b: YearSeries): { a: YearSeries; b: YearSeries } {
  const length = Math.max(a.points.length, b.points.length);
  return { a: padOne(a, length), b: padOne(b, length) };
}

@Component({
  selector: 'app-money-mortgage',
  templateUrl: './mortgage.component.html',
  styleUrls: ['./mortgage.component.scss'],
  standalone: false,
})
export class MortgageComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';

  settings: MortgageSettings = { ...DEFAULT_MORTGAGE };

  /**
   * Dollar fields as typed. Held as strings rather than bound through a
   * formatting getter/setter pair: a setter that reformats on every keystroke
   * rewrites the input mid-edit and throws the caret to the end, so "1870"
   * becomes unenterable. Seeded once from storage, parsed to cents on commit.
   */
  draft = { principal: '', extra: '' };

  /** APR as typed, in percent. The model stores basis points. */
  rateDraft = '';

  /**
   * Everything derived, computed ONCE per input event by recompute().
   *
   * These are plain fields, not getters, on purpose. `amortize` walks up to
   * 720 months and a dozen call sites read it; behind a getter the whole walk
   * would re-run several thousand times per keystroke, because Angular
   * evaluates every template binding on every change-detection pass.
   */
  paymentCents = 0;
  schedule: MonthRow[] = [];
  yearRows: YearRow[] = [];
  payoffDate = '';
  totalInterestCents = 0;
  monthsSaved = 0;
  interestSavedCents = 0;
  /**
   * True when the extra payment is the ONLY reason this loan ever pays off:
   * the with-extra schedule amortizes but the scheduled-payment-only
   * schedule never does (`amortize` returns `[]` for it). In that state
   * "months/interest saved" is meaningless — there is no base payoff to
   * compare against — so the template must say that instead of rendering
   * zeros, which would read as "the extra payment achieved nothing".
   */
  extraIsWhatPaysItOff = false;
  points: LinePoint[] = [];
  displayValues: string[] = [];
  comparePoints: LinePoint[] = [];
  compareDisplayValues: string[] = [];

  readonly fmt = formatCents;
  readonly maxMonths = SIM_MAX_MONTHS;

  private uid = '';
  private seeded = false;
  private subs: Subscription[] = [];
  private innerSubs: Subscription[] = [];

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      // Reset here, not just at field init: a sign-out then sign-in within
      // this same instance re-enters the handler, and the next user's stored
      // settings must seed the text fields too.
      this.seeded = false;

      this.innerSubs.forEach(s => s.unsubscribe());
      this.innerSubs = [];

      this.innerSubs.push(this.money.subscribeMortgage(this.uid).subscribe({
        next: settings => {
          this.settings = { ...settings };
          if (!this.seeded) {
            this.draft = {
              principal: (settings.principalCents / 100).toFixed(2),
              extra: (settings.extraMonthlyCents / 100).toFixed(2),
            };
            this.rateDraft = (settings.rateBasisPoints / 100).toFixed(3)
              .replace(/\.?0+$/, '');
            this.seeded = true;
          }
          this.recompute();
          this.loading = false;
        },
        error: (e: unknown) => this.fail(e),
      }));
    }));
  }

  // The authState subscription is kept alive for the life of the component
  // (an unstored one outlives it and re-runs against a destroyed instance on
  // the next auth event); innerSubs holds the per-login stream.
  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.innerSubs.forEach(s => s.unsubscribe());
  }

  private fail(e: unknown): void {
    this.error = `Could not load your mortgage: ${(e as Error).message}. Try reloading the page.`;
    this.loading = false;
  }

  /**
   * `termMonths` bounded and integral.
   *
   * [(ngModel)] writes the model on every keystroke but commit() clamps on
   * (change) — i.e. on blur. Bounding here, at the point recompute() reads
   * it, keeps a pasted six-digit term from reaching amortize before blur
   * ever fires. Same reasoning as boundedYears in savings.component.ts.
   */
  get boundedTerm(): number {
    const n = Math.round(this.settings.termMonths);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, this.maxMonths);
  }

  get hasLoan(): boolean { return this.settings.principalCents > 0; }

  /** True when the payment cannot cover one month of interest. */
  get stalled(): boolean { return this.hasLoan && this.schedule.length === 0; }

  /** Only worth overlaying when the two schedules actually differ. */
  get showCompare(): boolean { return this.settings.extraMonthlyCents > 0; }

  trackYear(_: number, y: YearRow): number { return y.year; }

  private interestOf(rows: MonthRow[]): number {
    return rows.reduce((s, r) => s + r.interestCents, 0);
  }

  /** Every figure on the screen, from one pair of schedule walks. */
  private recompute(): void {
    const bounded: MortgageSettings = { ...this.settings, termMonths: this.boundedTerm };
    const baseSchedule = amortize({ ...bounded, extraMonthlyCents: 0 });

    this.paymentCents = monthlyPayment(bounded);
    this.schedule = amortize(bounded);
    this.yearRows = annualRows(this.schedule);

    this.payoffDate = this.schedule.length
      ? targetDate(todayISO(), this.schedule.length) : '';
    this.totalInterestCents = this.interestOf(this.schedule);

    const comparable = this.schedule.length > 0 && baseSchedule.length > 0;
    this.monthsSaved = comparable ? baseSchedule.length - this.schedule.length : 0;
    this.interestSavedCents = comparable
      ? this.interestOf(baseSchedule) - this.totalInterestCents : 0;
    this.extraIsWhatPaysItOff = this.schedule.length > 0 && baseSchedule.length === 0;

    const baseYears = annualRows(baseSchedule);
    const main: YearSeries = {
      points: this.yearRows.map(y => ({ label: `${y.year}y`, value: y.balanceCents })),
      displayValues: this.yearRows.map(y => formatCents(y.balanceCents)),
    };
    const base: YearSeries = {
      points: baseYears.map(y => ({ label: `${y.year}y`, value: y.balanceCents })),
      displayValues: baseYears.map(y => formatCents(y.balanceCents)),
    };
    // Pad both to the longer schedule's length before handing them to the
    // chart — see padToSameHorizon for why unequal lengths would otherwise
    // draw two different payoff horizons across the same width.
    const padded = padToSameHorizon(main, base);
    this.points = padded.a.points;
    this.displayValues = padded.a.displayValues;
    this.comparePoints = padded.b.points;
    this.compareDisplayValues = padded.b.displayValues;
  }

  /** Live preview while typing, before (change) commits: parse and recompute
   *  without saving. */
  preview(): void {
    this.settings.principalCents = toCents(this.draft.principal);
    this.settings.extraMonthlyCents = toCents(this.draft.extra);
    this.settings.rateBasisPoints = toBasisPoints(this.rateDraft);
    this.recompute();
  }

  /**
   * Parse the text fields into the cents/basis-point model, then persist.
   *
   * Called from (change), which fires on blur for text inputs — not per
   * keystroke, which would be one RTDB write per character.
   */
  async commit(): Promise<void> {
    this.preview();
    // Clamp the persisted value. Not redundant with the recompute() inside
    // preview(): recompute() already reads the term through boundedTerm into
    // its own local copy, so this assignment doesn't change what's on
    // screen — but it IS what gets written to Firebase below, so an
    // out-of-range paste is clamped before it's saved, not just before it's
    // amortized.
    this.settings.termMonths = this.boundedTerm;

    if (!this.uid) return;
    try { await this.money.saveMortgage(this.uid, this.settings); }
    catch (e) { this.fail(e); }
  }
}
